<template>
  <div class="m-list">
    <div class="m-search">
      <i class="material-icons">search</i>
      <input v-model="keywords" :placeholder="$t('mobile.searchPlaceholder')" type="search" @input="onSearchInput" />
      <button v-if="keywords" class="m-search__clear" @click="clearSearch" :aria-label="$t('mobile.clear')">
        <i class="material-icons">close</i>
      </button>
    </div>

    <div class="m-chips">
      <button class="m-chip" :class="{ 'm-chip--on': !recordStatusId }" @click="selectStatus(null)">
        {{ $t("mobile.all") }}
      </button>
      <button v-for="s in statuses" :key="s.id" class="m-chip" :class="{ 'm-chip--on': recordStatusId === s.id }" @click="selectStatus(s.id)">
        {{ s.name }}
      </button>
    </div>

    <div v-if="loading && !items.length" class="m-hint">{{ $t("mobile.loading") }}</div>
    <div v-else-if="!items.length" class="m-hint">{{ $t("mobile.noResults") }}</div>

    <ul class="m-cards">
      <li v-for="item in items" :key="item.shipmentRecordId">
        <router-link :to="`/m/lieferplan/${item.shipmentRecordId}`" class="m-card" :class="statusClass(item)">
          <div class="m-card__head">
            <span class="m-card__title">{{ item.shipmentRecordName || "—" }}</span>
            <span v-if="can('ChinaUser') && item.chinaUser" class="m-card__avatar" :style="{ borderColor: item.chinaStatusColor || '#bdbdbd' }">
              <user-avatar v-if="item.chinaUserAvatar" :user-id="item.chinaUser" />
              <i v-else class="material-icons">person</i>
            </span>
          </div>

          <p v-if="supplierLine(item)" class="m-card__sub">{{ supplierLine(item) }}</p>

          <div v-if="can('ContainerId') && item.containerId" class="m-card__row">
            <img v-if="carrierLogo(item)" :src="carrierLogo(item)" class="m-card__logo" alt="" />
            <i v-else class="material-icons">sailing</i>
            <span>{{ item.containerId }}<template v-if="can('ContainerSize') && item.containerSize"> · {{ item.containerSize }}</template></span>
          </div>

          <div v-if="arrivalLine(item)" class="m-card__row">
            <img v-if="placeLogo(item)" :src="placeLogo(item)" class="m-card__logo" alt="" />
            <i v-else class="material-icons">warehouse</i>
            <span>{{ arrivalLine(item) }}</span>
          </div>

          <span v-if="can('RecordStatus') && item.recordStatus" class="m-card__status">{{ item.recordStatus }}</span>
        </router-link>
      </li>
    </ul>

    <button v-if="items.length < total" class="m-more" :disabled="loading" @click="loadMore">
      {{ loading ? $t("mobile.loading") : $t("mobile.loadMore", { count: total - items.length }) }}
    </button>
    <p v-else-if="items.length" class="m-hint m-hint--small">{{ $t("mobile.countAll", { count: total }) }}</p>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import axiosFactory from "@/services/axios.factory";
import { UserAvatar } from "@/components";
import { hasPermission } from "@/mixins/auth";
import { loadMasterData, carrierLogo, placeLogo } from "@/helpers/shippingMasterData";

const PAGE_SIZE = 20;

// Dieselbe Zuordnung wie am Desktop (ShippingPlans.vue rowClassCallback + ShippingPlans.css):
// der Status faerbt die ganze Zeile, mobil die ganze Karte.
const STATUS_CLASS = {
  "Delivery date in planning": "m-card--planning",
  "Delivery Date fixed": "m-card--fixed",
  Delivered: "m-card--delivered",
  Archived: "m-card--archived",
};

export default {
  name: "MobileShippingPlanList",
  components: { UserAvatar },
  data() {
    return {
      items: [],
      statuses: [],
      masterData: { carriers: [], places: [] },
      total: 0,
      pageIndex: 0,
      keywords: "",
      recordStatusId: null,
      loading: false,
    };
  },
  created() {
    this.onSearchInput = debounce(this.reload, 350);
    this.loadStatuses();
    this.loadLogos();
    this.reload();
  },
  methods: {
    can(column) {
      return hasPermission("LimitRead", "ShippingPlans", column);
    },
    supplierLine(item) {
      const parts = [];
      if (this.can("SupplierName") && item.supplierName) parts.push(item.supplierName);
      if (this.can("SupplierOrderId") && item.supplierOrderId) parts.push(item.supplierOrderId);
      return parts.join(" · ");
    },
    arrivalLine(item) {
      const parts = [];
      if (this.can("ContainerArrivalPlace") && item.containerArrivalPlace) parts.push(item.containerArrivalPlace);
      if (this.can("ContainerArrivalDate") && item.containerArrivalDate) {
        parts.push(this.$options.filters.formatDateTime(item.containerArrivalDate));
      }
      return parts.join(" · ");
    },
    statusClass(item) {
      return STATUS_CLASS[item.recordStatus] || "";
    },
    carrierLogo(item) {
      return carrierLogo(this.masterData, item.carrierNameId);
    },
    placeLogo(item) {
      return placeLogo(this.masterData, item.containerArrivalPlaceId);
    },
    async loadLogos() {
      this.masterData = await loadMasterData();
    },
    selectStatus(id) {
      this.recordStatusId = id;
      this.reload();
    },
    clearSearch() {
      this.keywords = "";
      this.reload();
    },
    async loadStatuses() {
      try {
        const res = await axiosFactory().get("/shipping_data/record_statuses");
        this.statuses = (res.data && res.data.data) || [];
      } catch (e) {
        this.statuses = [];
      }
    },
    async reload() {
      this.pageIndex = 0;
      this.items = [];
      await this.fetch();
    },
    async loadMore() {
      this.pageIndex += 1;
      await this.fetch();
    },
    async fetch() {
      this.loading = true;
      try {
        const res = await axiosFactory().get("/shipping_plans", {
          params: {
            // compact spart die verknuepften Produkte und Dokumente — die Liste zeigt sie nicht,
            // und sie machen den groessten Teil der Antwort aus.
            compact: true,
            pageSize: PAGE_SIZE,
            pageIndex: this.pageIndex,
            keywords: this.keywords || undefined,
            recordStatusId: this.recordStatusId || undefined,
            excludeArchived: true,
          },
        });
        const payload = res.data || {};
        this.items = this.items.concat(payload.data || []);
        this.total = payload.total || 0;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.m-list {
  padding: 12px 12px 24px;
}

.m-search {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border-radius: 10px;
  padding: 0 10px;
  height: 44px;

  i {
    color: #9e9e9e;
    font-size: 20px;
  }

  input {
    flex: 1;
    border: 0;
    outline: none;
    font-size: 15px;
    background: transparent;
    min-width: 0;
  }

  &__clear {
    border: 0;
    background: none;
    padding: 0;
    display: flex;
    cursor: pointer;
  }
}

.m-chips {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding: 10px 0 2px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
}

.m-chip {
  flex: 0 0 auto;
  border: 1px solid #e0e0e0;
  background: #fff;
  color: #616161;
  border-radius: 14px;
  padding: 5px 12px;
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;

  &--on {
    background: #4caf50;
    border-color: #4caf50;
    color: #fff;
  }
}

.m-cards {
  list-style: none;
  margin: 10px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

// Selektor bewusst mit Elternklasse: das Theme setzt `.md-theme-default a:not(.md-button)` auf
// die Akzentfarbe und wuerde eine einfache Klassenregel schlagen.
// Radius und Schatten sind aus dem Desktop-Theme uebernommen (.md-card).
.m-cards a.m-card {
  display: block;
  background: #fff;
  border-radius: 6px;
  padding: 12px 14px;
  color: #212121;
  text-decoration: none;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.14);

  &:hover,
  &:focus {
    color: #212121;
    text-decoration: none;
  }

  // Statusfarben wie in der Desktop-Tabelle (ShippingPlans.css, tr.<status> td)
  &.m-card--planning {
    background: #cbc3e3;
  }

  &.m-card--fixed {
    background: lightgreen;
  }

  &.m-card--delivered {
    background: lightblue;
  }

  &.m-card--archived {
    background: #bdbdbd;
  }
}

.m-card {
  &__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
  }

  &__title {
    font-size: 15px;
    font-weight: 500;
    line-height: 1.3;
  }

  &__avatar {
    flex: 0 0 auto;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    border: 3px solid #bdbdbd;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    i {
      font-size: 20px;
      color: #bdbdbd;
    }
  }

  &__status {
    display: inline-block;
    margin-top: 9px;
    font-size: 11px;
    background: rgba(255, 255, 255, 0.75);
    border-radius: 3px;
    padding: 2px 8px;
    color: #424242;
  }

  &__sub {
    font-size: 13px;
    color: #616161;
    margin: 3px 0 0;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
    font-size: 13px;
    color: #616161;

    i {
      font-size: 18px;
      color: #757575;
    }
  }

  &__logo {
    height: 18px;
    width: 18px;
    object-fit: contain;
    flex: 0 0 auto;
  }
}

.m-more {
  width: 100%;
  margin-top: 14px;
  height: 44px;
  border: 1px solid #c8e6c9;
  background: #fff;
  color: #4caf50;
  border-radius: 10px;
  font-size: 14px;
  cursor: pointer;
}

.m-hint {
  text-align: center;
  color: #9e9e9e;
  font-size: 14px;
  padding: 32px 0;

  &--small {
    padding: 16px 0 0;
    font-size: 12px;
  }
}
</style>
